import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import MiniTitle from "layouts/MiniTitle";
import MainTitle from "layouts/MainTitle";
import "styles/public.css";
import continentImg from "assets/layout_img/continent.svg";
import { FaArrowRight } from "react-icons/fa6";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const API_URL =
  "https://algoritm-api-edu.uz/api/public/MainPageUi/GetAllEvents";
const BASE_IMG_URL = "https://algoritm-api-edu.uz/";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const [y, m, d] = dateString.slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
};

const formatTime = (start, end) => {
  if (!start) return "00:00";
  const s = start.slice(11, 16);
  if (!end || end === start) return s;
  const e = end.slice(11, 16);
  return `${s} – ${e}`;
};

const getLocationInfo = (location) => {
  if (!location) return { text: "Onlayn tadbir", url: null, isLink: false };

  const isLink = /http|maps\.yandex|google\.com/.test(location);
  return {
    text: isLink ? "Xaritada ko‘rish" : location,
    url: isLink ? location : null,
    isLink,
  };
};

const ExpandableText = memo(({ text }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text || text.trim() === "")
    return <p className="meeting-desc no-desc">Tavsif mavjud emas</p>;

  const needsExpand = text.length > 140 || text.split("\n").length > 3;

  return (
    <div className="expandable-desc">
      <div className={`text-wrapper ${expanded ? "expanded" : ""}`}>
        <p className="meeting-desc">{text}</p>
        {needsExpand && !expanded && <div className="fade-overlay"></div>}
      </div>

      {needsExpand && (
        <motion.button
          onClick={() => setExpanded((v) => !v)}
          className="more-btn"
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          aria-label={expanded ? "Kamaytirish" : "Batafsil o‘qish"}
        >
          {expanded ? "Kamroq" : "Ko‘proq ko‘rish"}
        </motion.button>
      )}
    </div>
  );
});
ExpandableText.displayName = "ExpandableText";

const EventCard = memo(({ event, index = 0 }) => {
  const {
    text: locText,
    url: locUrl,
    isLink,
  } = getLocationInfo(event.location);
  const isPast = event.endTime && new Date(event.endTime) < new Date();
  const imageUrl = event.imageUrl ? `${BASE_IMG_URL}${event.imageUrl}` : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{
        duration: 0.8,
        delay: index * 0.12,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ y: -10 }}
      className={`meeting-card ${isPast ? "past-event" : "upcoming-event"}`}
      role="article"
      aria-labelledby={`event-title-${event.id || index}`}
    >
      <div className="meeting-date">
        <p className="date">{formatDate(event.startTime)}</p>
        <p className="time">{formatTime(event.startTime, event.endTime)}</p>
      </div>

      <span className="meeting-stick" aria-hidden="true"></span>

      <div className="meeting-info">
        <h4 id={`event-title-${event.id || index}`} className="meeting-title">
          {event.title || "Nomsiz tadbir"}
        </h4>

        <ExpandableText text={event.description} />

        <motion.div
          className={`meeting-location ${isLink ? "clickable-location" : ""}`}
          role={isLink ? "link" : undefined}
          tabIndex={isLink ? 0 : -1}
          onClick={() =>
            isLink && window.open(locUrl, "_blank", "noopener,noreferrer")
          }
          onKeyDown={(e) => {
            if (isLink && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              window.open(locUrl, "_blank", "noopener,noreferrer");
            }
          }}
          whileHover={isLink ? { x: 8 } : {}}
          whileTap={isLink ? { scale: 0.96 } : {}}
        >
          <img src={continentImg} alt="" className="continent-img" />
          <p className="continent">{locText}</p>
          {isLink && <FaArrowRight className="location-arrow" />}
        </motion.div>
      </div>

      {imageUrl && (
        <motion.div
          className="meeting-img"
          style={{ backgroundImage: `url(${imageUrl})` }}
          initial={{ opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.12 + 0.3 }}
          aria-hidden="true"
        />
      )}
    </motion.div>
  );
});
EventCard.displayName = "EventCard";

const Events = memo(() => {
  const [allEvents, setAllEvents] = useState([]);
  const [displayedEvents, setDisplayedEvents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);

  const ITEMS_PER_PAGE = 4;

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(API_URL, {
        search: "",
        sortBy: "",
        orderType: "",
        pageSize: 200,
        page: 1,
      });

      const valid = (data?.rows || [])
        .filter((e) => e.startTime)
        .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

      setAllEvents(valid);
      setDisplayedEvents(valid.slice(0, ITEMS_PER_PAGE));
    } catch (err) {
      console.error("Tadbirlar yuklashda xato:", err);
      setAllEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (showAll) {
      const start = (currentPage - 1) * ITEMS_PER_PAGE;
      setDisplayedEvents(allEvents.slice(start, start + ITEMS_PER_PAGE));
    } else {
      setDisplayedEvents(allEvents.slice(0, ITEMS_PER_PAGE));
      setCurrentPage(1);
    }
  }, [allEvents, currentPage, showAll]);

  const totalPages = Math.ceil(allEvents.length / ITEMS_PER_PAGE);

  const renderedEvents = useMemo(() => {
    return displayedEvents.map((event, idx) => (
      <EventCard
        key={event.id || `${event.startTime}-${idx}`}
        event={event}
        index={showAll ? (currentPage - 1) * 4 + idx : idx}
      />
    ));
  }, [displayedEvents, showAll, currentPage]);

  const toggleShowAll = useCallback(() => setShowAll((prev) => !prev), []);

  return (
    <section className="meeting" id="events" aria-labelledby="events-title">
      <div className="container">
        <MiniTitle>Tadbirlar</MiniTitle>
        <MainTitle id="events-title">Barcha tadbirlar</MainTitle>

        <motion.div className="meeting-cards-wrapper">
          {loading ? (
            <div className="loading-grid">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="skeleton-card" />
              ))}
            </div>
          ) : allEvents.length === 0 ? (
            <p className="no-events">Hozircha tadbirlar mavjud emas</p>
          ) : (
            <AnimatePresence mode="popLayout">{renderedEvents}</AnimatePresence>
          )}
        </motion.div>

        <AnimatePresence>
          {showAll && totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="pagination"
            >
              {Array.from({ length: totalPages }, (_, i) => (
                <motion.button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={currentPage === i + 1 ? "active" : ""}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {i + 1}
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {allEvents.length > ITEMS_PER_PAGE && (
          <motion.div className="btn-center">
            <motion.button
              className="meeting-button"
              onClick={toggleShowAll}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {showAll ? "Kamaytirish" : "Barchasini ko‘rish"}
              <motion.span
                className="arrow-icon"
                animate={{ x: showAll ? -8 : 12 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <FaArrowRight />
              </motion.span>
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  );
});

Events.displayName = "EventsSection";
export default Events;
